import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api, type AgentSummary, type Me } from "../lib/api";
import { errorText, wabaApi, type ChannelType, type Connection } from "../lib/wabaApi";
import { BRAND, BrandTile, type BrandKey } from "../components/Brand";
import { EmbeddedSignup } from "../components/EmbeddedSignup";
import { MetaLogin } from "../components/MetaLogin";
import { useBind, useChannels } from "../components/Channels";
import { Badge, Modal, btnGhost, btnPrimary, card, inp, toast } from "../components/ui";

// Conexões da conta: UMA conexão WABA por conta (criada aqui na primeira visita),
// com uma badge por canal. WhatsApp/Instagram/Messenger escolhem o agente por
// canal; E-mail atende com o agente do WhatsApp (claimed_by_agent_id); OLX e
// Mercado Livre só conectam — leads e conversas vão pra equipe Carmillion
// (webhooks v3), sem IA. Decisões 2026-10-26.

const ORDEM: BrandKey[] = ["whatsapp", "instagram", "messenger", "email", "olx", "meli"];
const BIND: Partial<Record<BrandKey, ChannelType>> = { whatsapp: "waba", instagram: "instagram", messenger: "messenger" };
const UNLINK: Record<BrandKey, string> = { whatsapp: "disconnect", instagram: "instagram", messenger: "messenger", email: "email", olx: "olx", meli: "meli" };

const TOGGLES: { field: "typing_indicator" | "auto_mark_read" | "auto_split_text"; label: string }[] = [
  { field: "typing_indicator", label: "Mostrar “digitando”" },
  { field: "auto_mark_read", label: "Marcar recebidas como lidas" },
  { field: "auto_split_text", label: "Separar respostas longas" },
];

function estado(k: BrandKey, c: Connection): { on: boolean; alerta: boolean; ident: string | null } {
  switch (k) {
    case "whatsapp": return { on: !!c.phone_number_id, alerta: !!c.phone_number_id && !!c.meta_disconnected_at, ident: c.display_name || c.phone_number };
    case "instagram": return { on: !!c.ig_user_id, alerta: false, ident: c.ig_username ? `@${c.ig_username}` : null };
    case "messenger": return { on: c.messenger_active, alerta: false, ident: c.page_name };
    case "email": return { on: !!c.email_address, alerta: false, ident: c.email_address };
    case "olx": return { on: !!c.olx_status, alerta: c.olx_status === "reconnect", ident: c.olx_account_name || c.olx_account_email };
    case "meli": return { on: !!c.meli_status, alerta: c.meli_status === "reconnect", ident: c.meli_nickname };
  }
}

function hint(k: BrandKey): string {
  switch (k) {
    case "whatsapp": return "Número do WhatsApp Business da loja.";
    case "instagram": return "Directs do seu Instagram profissional.";
    case "messenger": return "Mensagens da sua Página do Facebook.";
    case "email": return "Leads de Webmotors, iCarros e outros portais por e-mail.";
    case "olx": return "Chat e leads dos seus anúncios na OLX.";
    case "meli": return "Perguntas e leads dos seus anúncios no Mercado Livre.";
  }
}

export function Connections() {
  const qc = useQueryClient();
  const { data: me } = useQuery<Me>({ queryKey: ["me"], queryFn: () => api.get("/me") });
  const { conns, isSuccess, bindsLoading, error, channels } = useChannels();
  const { data: agents = [] } = useQuery<AgentSummary[]>({ queryKey: ["agents"], queryFn: () => api.get("/agents") });
  const bind = useBind();
  const [modal, setModal] = useState<null | "whatsapp" | "instagram" | "messenger">(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["waba-conns"] });
  // A lista vem da mais nova pra mais antiga: fica com a que tem canal, senão a mais antiga
  const conn: Connection | undefined = conns.find((c) => ORDEM.some((k) => estado(k, c).on)) ?? conns[conns.length - 1];

  // ponytail: conexão única criada na 1ª visita; só com a lista carregada OK (lista com erro
  // criaria duplicata); isIdle = uma tentativa por montagem, duas abas esbarram no 409 da waba
  const create = useMutation({
    mutationFn: () => wabaApi.post<Connection>("/connections", { name: me!.name }),
    onSettled: refresh,
  });
  useEffect(() => {
    if (isSuccess && conns.length === 0 && me && create.isIdle) create.mutate();
  }, [isSuccess, conns.length, me, create.isIdle]);  // eslint-disable-line react-hooks/exhaustive-deps

  const patch = useMutation({
    mutationFn: (body: Partial<Connection>) => wabaApi.patch(`/connections/${conn!.id}`, body),
    onSettled: refresh,
    onError: (e) => toast(errorText(e, "Erro ao salvar")),
  });
  const unlink = useMutation({
    mutationFn: (k: BrandKey) => k === "whatsapp" ? wabaApi.post(`/connections/${conn!.id}/disconnect`) : wabaApi.delete(`/connections/${conn!.id}/${UNLINK[k]}`),
    onSettled: refresh,
    onSuccess: () => toast("Canal desconectado"),
    onError: (e) => toast(errorText(e, "Erro ao desconectar")),
  });
  const email = useMutation({
    mutationFn: () => wabaApi.post(`/connections/${conn!.id}/email`),
    onSettled: refresh,  // 400 "já tem e-mail" (corrida) também recarrega
    onSuccess: () => toast("Endereço criado"),
    onError: (e) => toast(errorText(e, "Erro ao criar o e-mail")),
  });
  const link = useMutation({
    // janela aberta NO clique (antes do await): Safari/bloqueador recusa pop-up aberto depois
    mutationFn: async (p: { k: "olx" | "meli"; win: Window | null }) => {
      const r = await wabaApi.post<{ url: string }>(`/connections/${conn!.id}/${p.k}/link`);
      if (new URL(r.url).protocol !== "https:") throw new Error("Link inválido");
      if (p.win && !p.win.closed) { p.win.opener = null; p.win.location.href = r.url; }
      else if (!p.win) window.location.href = r.url;  // pop-up bloqueado: navega na própria aba
    },
    onError: (e, p) => { p.win?.close(); toast(errorText(e, "Erro ao gerar o link")); },
  });
  // OLX/ML autorizam em outra janela: recarrega a lista a cada 4 s até a badge virar (teto 5 min)
  const linked = link.isSuccess && link.variables && conn ? estado(link.variables.k, conn).on : true;
  useEffect(() => {
    if (linked) return;
    const t = setInterval(refresh, 4000);
    const stop = setTimeout(() => clearInterval(t), 5 * 60_000);
    return () => { clearInterval(t); clearTimeout(stop); };
  }, [linked]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (error || create.isError) {
    return <div role="alert" style={{ padding: "28px 32px", color: "var(--danger)", fontSize: 13 }}>Não foi possível carregar suas conexões. <button style={{ ...btnGhost, marginLeft: 8 }} onClick={() => { create.reset(); refresh(); }}>Tentar de novo</button></div>;
  }
  if (!conn) {
    return <div style={{ padding: "28px 32px", color: "var(--ink-muted)", fontSize: 13 }}>Preparando sua conexão…</div>;
  }

  const nameOf = (id: string | null) => agents.find((a) => a.id === id)?.name ?? null;
  const agentOf = (k: BrandKey) => channels.find((ch) => ch.conn.id === conn.id && ch.type === BIND[k])?.agentId ?? null;
  const waAgent = nameOf(conn.claimed_by_agent_id);
  const busy = unlink.isPending || email.isPending || link.isPending;

  return (
    <div style={{ padding: "28px 32px 60px" }}>
      <h1 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 600, letterSpacing: "-.01em" }}>Conexões</h1>
      <p style={{ marginTop: 4, marginBottom: 22, color: "var(--ink-muted)", fontSize: 14 }}>Canais que atendem com seus agentes. Conecte os que usar e escolha o agente de cada um.</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 }}>
        {ORDEM.map((k) => {
          const b = BRAND[k];
          const { on, alerta, ident } = estado(k, conn);
          const bindType = BIND[k];
          const agentId = bindType ? agentOf(k) : null;
          const connect = () => {
            if (k === "email") email.mutate();
            else if (k === "olx" || k === "meli") link.mutate({ k, win: window.open("", "_blank") });
            else setModal(k);
          };
          return (
            <section key={k} aria-labelledby={`canal-${k}`} style={{ ...card, padding: 16, display: "flex", flexDirection: "column", gap: 12, borderColor: alerta ? "var(--danger)" : on ? "var(--ok-line)" : "var(--line)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <BrandTile k={k} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <h2 id={`canal-${k}`} style={{ fontSize: 15, fontWeight: 600 }}>{b.nome}</h2>
                    {alerta ? <Badge tone="warn">Atenção</Badge> : on ? <Badge tone="ok">Conectado</Badge> : <Badge tone="neutral">Não conectado</Badge>}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--ink-dim)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: on && (k === "email" || k === "whatsapp") ? "var(--font-mono)" : undefined }}>
                    {on ? ident ?? "—" : hint(k)}
                  </div>
                </div>
              </div>

              {alerta && (
                <div style={{ fontSize: 12, color: "var(--danger)", background: "var(--danger-soft)", borderRadius: 8, padding: "8px 12px" }}>
                  {k === "whatsapp" ? `Desconectado na Meta. Reconecte com o mesmo número (${conn.phone_number ?? ""}); leads e configurações são preservados.` : `${b.nome} derrubou o acesso (senha trocada ou autorização revogada). Conecte de novo.`}
                </div>
              )}

              {on && bindType && (
                <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, fontWeight: 600, color: "var(--ink-secondary)" }}>
                  Agente que atende
                  <select style={inp} value={agentId ?? ""} disabled={bind.isPending || bindsLoading}
                    onChange={(e) => bind.mutate({ connection_id: conn.id, channel: bindType, agent_id: e.target.value || null, agent_name: nameOf(e.target.value || null) })}>
                    <option value="">— sem agente —</option>
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </label>
              )}
              {on && k === "whatsapp" && !alerta && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {TOGGLES.map((t) => (
                    <label key={t.field} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, fontSize: 12, cursor: "pointer" }}>
                      {t.label}
                      <input type="checkbox" checked={!!conn[t.field]} disabled={patch.isPending} onChange={(e) => patch.mutate({ [t.field]: e.target.checked })} style={{ accentColor: "var(--ok)", width: 16, height: 16 }} />
                    </label>
                  ))}
                </div>
              )}
              {on && k === "email" && (
                // ponytail: e-mail segue o agente do WhatsApp (claimed_by_agent_id no mailgun.py); select próprio exige canal "email" no ChannelBind
                <div style={{ fontSize: 12, color: waAgent ? "var(--ink-dim)" : "var(--warn)" }}>
                  {waAgent ? <>Atende com <strong>{waAgent}</strong>, o agente do WhatsApp.</> : "Vincule um agente ao WhatsApp para atender os e-mails."}
                </div>
              )}
              {on && (k === "olx" || k === "meli") && (
                <div style={{ fontSize: 12, color: "var(--ink-dim)" }}>Leads e conversas chegam para a equipe Carmillion.</div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
                {!on && <button style={btnPrimary} aria-label={`Conectar ${b.nome}`} disabled={busy} onClick={connect}>Conectar</button>}
                {on && alerta && <button style={btnPrimary} aria-label={`Reconectar ${b.nome}`} disabled={busy} onClick={connect}>Reconectar</button>}
                {on && k === "email" && <button style={btnGhost} onClick={() => { const p = navigator.clipboard?.writeText(ident ?? ""); if (p) p.then(() => toast("Endereço copiado"), () => toast("Não deu para copiar")); else toast("Não deu para copiar"); }}>Copiar endereço</button>}
                {on && <button style={{ ...btnGhost, color: "var(--danger)", marginLeft: "auto" }} aria-label={`Desconectar ${b.nome}`} disabled={busy} onClick={() => { if (confirm(`Desconectar ${b.nome}${ident ? ` (${ident})` : ""}? Os outros canais não são afetados.`)) unlink.mutate(k); }}>Desconectar</button>}
              </div>
            </section>
          );
        })}
      </div>

      {modal === "whatsapp" && (
        <Modal title="Conectar WhatsApp" onClose={() => setModal(null)}>
          <p style={{ fontSize: 13, color: "var(--ink-muted)", lineHeight: 1.5 }}>Faça login na Meta e escolha o número do WhatsApp Business que vai atender.</p>
          <EmbeddedSignup connectionId={conn.id} onSuccess={() => { refresh(); setModal(null); toast("WhatsApp conectado"); }} />
        </Modal>
      )}
      {(modal === "instagram" || modal === "messenger") && (
        <Modal title={`Conectar ${BRAND[modal].nome}`} onClose={() => setModal(null)}>
          <p style={{ fontSize: 13, color: "var(--ink-muted)", lineHeight: 1.5 }}>{modal === "instagram" ? "Faça login na Meta com a conta que administra a Página ligada ao seu Instagram profissional." : "Faça login na Meta com a conta que administra a Página do Facebook."}</p>
          <MetaLogin kind={modal} connectionId={conn.id} onSuccess={() => { refresh(); setModal(null); toast(`${BRAND[modal].nome} conectado`); }} />
        </Modal>
      )}
    </div>
  );
}
